import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { indexedDBManager } from '@creanizer/utils'
import type { SavedImagePrompt } from '@creanizer/types'

interface ImagePromptStore {
  // 保存済みプロンプト
  savedPrompts: SavedImagePrompt[]
  
  // 最大保存件数
  maxSavedPrompts: number
  
  // 初期化済みフラグ
  isInitialized: boolean
  
  // 初期化
  init: () => Promise<void>
  
  // プロンプト管理
  savePrompt: (prompt: SavedImagePrompt) => Promise<void>
  updatePrompt: (id: string, updates: Partial<SavedImagePrompt>) => Promise<void>
  deletePrompt: (id: string) => Promise<void>
  getPrompt: (id: string) => SavedImagePrompt | undefined
  
  // 一括操作
  deleteAllPrompts: () => Promise<void>
  exportPrompts: () => void
  importPrompts: (data: SavedImagePrompt[]) => Promise<void>
  exportAsText: () => void
  exportAsJSON: () => void
  importFromJSON: (jsonString: string) => Promise<void>
}

export const useImagePromptStore = create<ImagePromptStore>()(
  persist(
    (set, get) => ({
      savedPrompts: [],
      maxSavedPrompts: 1000,
      isInitialized: false,
      
      init: async () => {
        const { isInitialized } = get()
        if (isInitialized) return
        
        try {
          await indexedDBManager.init()
          
          // 既存のプロンプトを読み込み
          const prompts = await indexedDBManager.getAllImagePrompts()
          
          set({ 
            savedPrompts: prompts,
            isInitialized: true
          })
        } catch (error) {
          console.error('Failed to initialize ImagePromptStore:', error)
          // エラーでも動作は継続（メモリのみで動作）
          set({ savedPrompts: [], isInitialized: true })
        }
      },
      
      savePrompt: async (prompt) => {
        const { savedPrompts, maxSavedPrompts, isInitialized } = get()
        
        // 初期化されていない場合は初期化
        if (!isInitialized) {
          await get().init()
        }
        
        // 最大保存件数チェック
        if (savedPrompts.length >= maxSavedPrompts) {
          throw new Error(`保存できるプロンプトは最大${maxSavedPrompts}件までです`)
        }
        
        try {
          await indexedDBManager.saveImagePrompt(prompt)
          
          set(state => ({
            savedPrompts: [prompt, ...state.savedPrompts]
          }))
        } catch (error) {
          console.error('Failed to save prompt:', error)
          throw error
        }
      },
      
      updatePrompt: async (id, updates) => {
        const { savedPrompts, isInitialized } = get()
        
        // 初期化されていない場合は初期化
        if (!isInitialized) {
          await get().init()
        }
        
        const promptIndex = savedPrompts.findIndex(p => p.id === id)
        if (promptIndex === -1) return
        
        try {
          await indexedDBManager.updateImagePrompt(id, updates)
          
          const updatedPrompt = {
            ...savedPrompts[promptIndex],
            ...updates,
            updatedAt: new Date().toISOString()
          }
          
          set(state => {
            const newPrompts = [...state.savedPrompts]
            newPrompts[promptIndex] = updatedPrompt
            return { savedPrompts: newPrompts }
          })
        } catch (error) {
          console.error('Failed to update prompt:', error)
          throw error
        }
      },
      
      deletePrompt: async (id) => {
        const { savedPrompts, isInitialized } = get()
        
        // 初期化されていない場合は初期化
        if (!isInitialized) {
          await get().init()
        }
        
        try {
          await indexedDBManager.deleteImagePrompt(id)
          
          set(state => ({
            savedPrompts: state.savedPrompts.filter(p => p.id !== id)
          }))
        } catch (error) {
          console.error('Failed to delete prompt:', error)
          throw error
        }
      },
      
      getPrompt: (id) => {
        return get().savedPrompts.find(p => p.id === id)
      },
      
      deleteAllPrompts: async () => {
        const { isInitialized } = get()
        
        // 初期化されていない場合は初期化
        if (!isInitialized) {
          await get().init()
        }
        
        try {
          await indexedDBManager.deleteAllImagePrompts()
          
          set({ savedPrompts: [] })
        } catch (error) {
          console.error('Failed to delete all prompts:', error)
          throw error
        }
      },
      
      exportPrompts: () => {
        const { savedPrompts } = get()
        
        const dataStr = JSON.stringify(savedPrompts, null, 2)
        const dataBlob = new Blob([dataStr], { type: 'application/json' })
        const url = URL.createObjectURL(dataBlob)
        
        const link = document.createElement('a')
        link.href = url
        link.download = `image-prompts-${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        URL.revokeObjectURL(url)
      },
      
      importPrompts: async (data) => {
        const { maxSavedPrompts, isInitialized } = get()
        
        // 初期化されていない場合は初期化
        if (!isInitialized) {
          await get().init()
        }
        
        // バリデーション
        if (!Array.isArray(data)) {
          throw new Error('Invalid import data format')
        }
        
        if (data.length > maxSavedPrompts) {
          throw new Error(`インポートできるプロンプトは最大${maxSavedPrompts}件までです`)
        }
        
        try {
          // 既存データをクリア
          await get().deleteAllPrompts()
          
          // 新しいデータを保存
          const importedPrompts: SavedImagePrompt[] = []
          for (const prompt of data) {
            const newPrompt: SavedImagePrompt = {
              ...prompt,
              id: prompt.id || crypto.randomUUID(),
              createdAt: prompt.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
            await indexedDBManager.saveImagePrompt(newPrompt)
            importedPrompts.push(newPrompt)
          }
          
          // ストアを更新
          set({
            savedPrompts: importedPrompts.sort((a, b) => 
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            )
          })
        } catch (error) {
          console.error('Failed to import prompts:', error)
          throw error
        }
      },
      
      exportAsText: () => {
        const { savedPrompts } = get()
        if (savedPrompts.length === 0) {
          alert('保存された画像プロンプトがありません。')
          return
        }
        
        const content = formatPromptsForExport(savedPrompts)
        downloadTextFile(content, `saved_image_prompts_${Date.now()}.txt`)
      },
      
      exportAsJSON: () => {
        const { savedPrompts } = get()
        if (savedPrompts.length === 0) {
          alert('保存された画像プロンプトがありません。')
          return
        }
        
        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'imagePrompts',
          data: savedPrompts
        }
        
        downloadJSONFile(exportData, `saved_image_prompts_${Date.now()}.json`)
      },
      
      importFromJSON: async (jsonString: string) => {
        try {
          const importData = JSON.parse(jsonString)
          
          // バリデーション
          if (!importData.data && !Array.isArray(importData)) {
            throw new Error('無効なデータ形式です')
          }
          
          const newPrompts = (importData.data || importData) as SavedImagePrompt[]
          
          // importPromptsメソッドを再利用
          await get().importPrompts(newPrompts)
          alert(`${newPrompts.length}件の画像プロンプトをインポートしました。`)
        } catch (error) {
          console.error('Import error:', error)
          alert('インポートに失敗しました。ファイル形式を確認してください。')
        }
      }
    }),
    {
      name: 'image-prompt-storage',
      partialize: (state) => ({
        maxSavedPrompts: state.maxSavedPrompts
      })
    }
  )
)

// プロンプトをテキスト形式にフォーマット
function formatPromptsForExport(prompts: SavedImagePrompt[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  let content = `【保存された画像プロンプト一覧】
生成日時: ${date}
総数: ${prompts.length}件

`

  prompts.forEach((prompt, index) => {
    content += `\n【${index + 1}】 ${prompt.title}\n`
    content += '=' .repeat(50) + '\n\n'
    
    // 基本情報
    content += `■ 基本設定\n`
    content += `タイプ: ${prompt.input.imageType === 'portrait' ? '立ち絵' : 
                       prompt.input.imageType === 'illustration' ? '一枚絵・シーン' : '背景'}\n`
    if (prompt.input.targetModel) {
      content += `AIモデル: ${prompt.input.targetModel}\n`
    }
    if (prompt.input.composition) {
      content += `構図: ${prompt.input.composition}\n`
    }
    if (prompt.input.backgroundType) {
      content += `背景タイプ: ${prompt.input.backgroundType}\n`
    }
    if (prompt.input.backgroundColor) {
      content += `背景色: ${prompt.input.backgroundColor}\n`
    }
    content += `入力ソース: ${prompt.input.inputSource}\n`
    if (prompt.input.creativityLevel) {
      content += `創造性レベル: ${prompt.input.creativityLevel}/10\n`
    }
    if (prompt.input.useProMode) {
      content += `Proモード: 有効\n`
    }
    content += '\n'
    
    // 生成されたプロンプト
    content += `■ 生成されたプロンプト\n`
    if (prompt.result.positivePrompt) {
      content += `ポジティブプロンプト:\n`
      content += `${prompt.result.positivePrompt}\n\n`
    }
    if (prompt.result.negativePrompt) {
      content += `ネガティブプロンプト:\n`
      content += `${prompt.result.negativePrompt}\n\n`
    }
    
    // モデル固有パラメータ
    if (prompt.result.modelSpecificParams) {
      const params = prompt.result.modelSpecificParams
      content += `■ モデル固有パラメータ\n`
      if (params.aspectRatio) content += `  アスペクト比: ${params.aspectRatio}\n`
      if (params.stylize) content += `  スタイライズ: ${params.stylize}\n`
      if (params.chaos) content += `  カオス: ${params.chaos}\n`
      if (params.version) content += `  バージョン: ${params.version}\n`
      if (params.cfgScale) content += `  CFGスケール: ${params.cfgScale}\n`
      if (params.steps) content += `  ステップ数: ${params.steps}\n`
      if (params.sampler) content += `  サンプラー: ${params.sampler}\n`
      if (params.clipSkip) content += `  CLIPスキップ: ${params.clipSkip}\n`
      content += '\n'
    }
    
    // 詳細入力情報
    if (prompt.input.manualInput) {
      const mi = prompt.input.manualInput
      content += `■ 詳細設定\n`
      
      // 基本情報
      if (mi.characterName) content += `キャラクター名: ${mi.characterName}\n`
      if (mi.gender) content += `性別: ${mi.gender}\n`
      if (mi.age) content += `年齢: ${mi.age}\n`
      if (mi.ageGroup) content += `年齢層: ${mi.ageGroup}\n`
      if (mi.bodyType) content += `体型: ${mi.bodyType}\n`
      if (mi.bodyTypeDetail) content += `体型詳細: ${mi.bodyTypeDetail}\n`
      if (mi.expression) content += `表情: ${mi.expression}\n`
      
      // 髪の詳細
      if (mi.hair) {
        content += `\n髪の設定:\n`
        if (mi.hair.front) content += `  前髪: ${mi.hair.front}\n`
        if (mi.hair.style) content += `  髪型: ${mi.hair.style}\n`
        if (mi.hair.texture) content += `  髪質: ${mi.hair.texture}\n`
        if (mi.hair.color) content += `  髪色: ${mi.hair.color}\n`
        if (mi.hair.colorEffect) content += `  色効果: ${mi.hair.colorEffect}\n`
      }
      
      // 目の詳細
      if (mi.eyes) {
        content += `\n目の設定:\n`
        if (mi.eyes.shape) content += `  形状: ${mi.eyes.shape}\n`
        if (mi.eyes.size) content += `  大きさ: ${mi.eyes.size}\n`
        if (mi.eyes.color) content += `  色: ${mi.eyes.color}\n`
      }
      
      // 顔の詳細
      if (mi.faceDetails?.features && mi.faceDetails.features.length > 0) {
        content += `\n顔の特徴: ${mi.faceDetails.features.join('、')}\n`
      }
      
      // 服装詳細
      if (mi.clothing) {
        content += `\n服装:\n`
        if (mi.clothing.tops) content += `  トップス: ${mi.clothing.tops}\n`
        if (mi.clothing.topsColor) content += `  トップス色: ${mi.clothing.topsColor}\n`
        if (mi.clothing.bottoms) content += `  ボトムス: ${mi.clothing.bottoms}\n`
        if (mi.clothing.bottomsColor) content += `  ボトムス色: ${mi.clothing.bottomsColor}\n`
        if (mi.clothing.onepiece) content += `  ワンピース: ${mi.clothing.onepiece}\n`
        if (mi.clothing.onepieceColor) content += `  ワンピース色: ${mi.clothing.onepieceColor}\n`
        if (mi.clothing.shoes) content += `  靴: ${mi.clothing.shoes}\n`
        if (mi.clothing.shoesColor) content += `  靴色: ${mi.clothing.shoesColor}\n`
        if (mi.clothing.accessories && mi.clothing.accessories.length > 0) {
          content += `  アクセサリー: ${mi.clothing.accessories.join('、')}\n`
        }
      }
      
      // ポーズ
      if (mi.pose) {
        content += `\nポーズ:\n`
        if (mi.pose.standing) content += `  立ちポーズ: ${mi.pose.standing}\n`
        if (mi.pose.gesture) content += `  ジェスチャー: ${mi.pose.gesture}\n`
        if (mi.pose.viewAngle) content += `  視点: ${mi.pose.viewAngle}\n`
      }
      
      // 世界観
      if (mi.worldSetting) {
        content += `\n世界観:\n`
        if (mi.worldSetting.type) content += `  ジャンル: ${mi.worldSetting.type}\n`
        if (mi.worldSetting.era) content += `  時代: ${mi.worldSetting.era}\n`
        if (mi.worldSetting.location) content += `  場所: ${mi.worldSetting.location}\n`
        if (mi.worldSetting.atmosphere) content += `  雰囲気: ${mi.worldSetting.atmosphere}\n`
      }
      
      // 画風
      if (mi.artStyle) {
        content += `\n画風:\n`
        if (mi.artStyle.period) content += `  年代: ${mi.artStyle.period}\n`
        if (mi.artStyle.technique) content += `  タッチ: ${mi.artStyle.technique}\n`
        if (mi.artStyle.mood) content += `  雰囲気: ${mi.artStyle.mood}\n`
        if (mi.artStyle.detailLevel) content += `  詳細度: ${mi.artStyle.detailLevel}\n`
        if (mi.artStyle.customStyle) content += `  カスタム: ${mi.artStyle.customStyle}\n`
      }
      
      // 追加情報
      if (mi.additionalInfo) {
        content += `\n追加情報:\n`
        if (mi.additionalInfo.text) content += `  ${mi.additionalInfo.text}\n`
      }
      
      // 自由記述
      if (mi.freeText) {
        content += `\n自由記述:\n${mi.freeText}\n`
      }
      
      content += '\n'
    }
    
    // 追加タグ
    if (prompt.input.additionalTags && prompt.input.additionalTags.length > 0) {
      content += `追加タグ: ${prompt.input.additionalTags.join('、')}\n`
    }
    if (prompt.input.negativeTags && prompt.input.negativeTags.length > 0) {
      content += `ネガティブタグ: ${prompt.input.negativeTags.join('、')}\n`
    }
    
    // メタデータ
    if (prompt.result.metadata) {
      content += `\n生成情報:\n`
      content += `  生成日時: ${prompt.result.metadata.timestamp}\n`
      if (prompt.result.metadata.seed) content += `  シード値: ${prompt.result.metadata.seed}\n`
    }
    
    // メモとタグ
    if (prompt.memo) {
      content += `\nメモ: ${prompt.memo}\n`
    }
    
    if (prompt.tags && prompt.tags.length > 0) {
      content += `タグ: ${prompt.tags.join(', ')}\n`
    }
    
    content += '\n' + '-'.repeat(50) + '\n'
  })

  return content
}

// テキストファイルをダウンロード
function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// JSONファイルをダウンロード
function downloadJSONFile(data: any, filename: string) {
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}